import { useMemo } from "react";

import { toast } from "sonner";

import { Label } from "src/components/ui/label";
import { Switch } from "src/components/ui/switch";
import { useNotifications } from "src/lib/hooks/useNotifications";

const preferenceConfig = {
  MARKETING_EMAILS: {
    label: "Marketing emails",
    description: "Product updates, feature announcements, and related emails.",
    defaultEnabled: true,
  },
  FAILED_INTEGRATION_EMAILS: {
    label: "Failed integration alerts",
    description:
      "Email alerts when an integration starts failing to pull data.",
    defaultEnabled: true,
  },
};

const getPreferenceEnabled = (preferences, type, defaultEnabled = false) =>
  preferences.find(
    (preference) => preference.type === type && preference.channel === "EMAIL"
  )?.enabled ?? defaultEnabled;

const NotificationSettings = () => {
  const { preferences, loading, updateNotificationPreference } =
    useNotifications({
      includeNotifications: false,
      includeHistory: false,
      includeUnreadCount: false,
      includePreferences: true,
    });

  const orderedPreferences = useMemo(
    () =>
      Object.entries(preferenceConfig).map(([type, config]) => ({
        type,
        ...config,
        enabled: getPreferenceEnabled(preferences, type, config.defaultEnabled),
      })),
    [preferences]
  );

  const handleTogglePreference = async (type, enabled) => {
    try {
      await updateNotificationPreference({
        type,
        channel: "EMAIL",
        enabled,
      });

      toast.success("Notification preference updated");
    } catch (_error) {
      // Error toast is handled centrally in useApiMutation.
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold">Email Preferences</h3>
        <p className="text-muted-foreground text-sm">
          Choose which emails you want us to send.
        </p>
      </div>

      <div className="space-y-4">
        {orderedPreferences.map((preference) => (
          <div
            key={preference.type}
            className="flex items-center justify-between gap-4 rounded-lg border p-4"
          >
            <div className="space-y-1">
              <Label htmlFor={preference.type} className="text-base">
                {preference.label}
              </Label>
              <p className="text-muted-foreground text-sm">
                {preference.description}
              </p>
            </div>
            <Switch
              id={preference.type}
              checked={preference.enabled}
              onCheckedChange={(checked) =>
                handleTogglePreference(preference.type, checked)
              }
              disabled={loading}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default NotificationSettings;
